import { getTabCoordinator } from './tabCoordinator'

const poPollingChannel = 'esearcher_po_polling'
const millisecondsPerMinute = 60 * 1000
const keepAliveThresholdMinutes = 9
const keepAliveCheckIntervalMinutes = 1
const poLastRequestAtKey = 'po-last-request-at'

const instance = {
    xhr: null,
    listeners: {},
    status: false,
    poRequestTimeout: 6000, // Max time (in ms) that one PO Request should take
    tabCoordinator: null,
    keepAliveIntervalId: null,

    /**
     * Send a call to the backend to receive new data
     *
     * @param {string} name
     * @param {object} data
     * @param {function} callback
     * @param {string} methodeName
     * @returns {void}
     */
    emit: function (name, data, callback, methodeName) {
        const self = this
        const callData = {}
        callData[name] = data
        const transportData = {
            caller: JSON.stringify(callData),
        }

        this._markPoRequest()
        this.xhr = $.ajax({
            url: '/po/' + this.uniqueUrlParam(),
            method: 'POST',
            data: transportData,
            success: function (data) {
                if (typeof callback == 'function') {
                    if (methodeName !== undefined) {
                        name += '-' + methodeName
                    }
                    callback(data.receiver[name])
                }
                self._receive(data)
                self.xhr = null
            },
        })
    },
    /**
     * Abort a pending xhr request from the connect request
     *
     * @returns {void}
     */
    abort: function () {
        // It needs to be checked with '!=' instead of '!==', otherwise it will not work properly
        if (typeof this.xhr !== 'undefined' && this.xhr != null) {
            this.xhr.abort()
        }

        this.xhr = null
    },
    /**
     * suspend a listener
     *
     * @param {string} name
     * @returns {boolean}
     */
    suspend: function (name) {
        let result = false
        if (this.listeners.hasOwnProperty(name)) {
            this.listeners[name].deactivate()
            this._removeListenerItemsFromLocalStorage(name)
            result = true
        }

        return result
    },
    /**
     * Ree-enables a listener.
     *
     * @param {string} name
     * @return {boolean}
     */
    resume: function (name) {
        let result = false
        if (this.listeners.hasOwnProperty(name)) {
            this.listeners[name].activate()
            result = true
        } else {
            console.warn("Listener '" + name + "' not found!")
        }

        return result
    },
    /**
     * Re-enables a listener and executes it.
     *
     * @param {string} listener
     */
    resumeAndRunListener: function (listener) {
        if (this.resume(listener) === true) {
            this.runPolling(listener)
        }
    },
    /**
     * Remove a listener from the stack
     *
     * @param {string} receiver
     * @returns {boolean}
     */
    remove: function (receiver) {
        let status = false
        if (typeof this.listeners[receiver] !== 'undefined') {
            this._removeListenerItemsFromLocalStorage(receiver)
            delete this.listeners[receiver]
            status = true
        }

        return status
    },
    /**
     * Return unique url parameter
     *
     * @returns {string}
     */
    uniqueUrlParam: function () {
        return this._hashString(window.location.pathname)
    },
    /**
     * @param {string} value
     * @returns {string}
     */
    _hashString: function (value) {
        var hash = 0x811c9dc5
        for (var i = 0; i < value.length; i++) {
            hash ^= value.charCodeAt(i)
            hash = Math.imul(hash, 0x01000193)
        }

        return (hash >>> 0).toString(16)
    },
    /**
     * @returns {object}
     */
    _getTabCoordinator: function () {
        if (this.tabCoordinator === null) {
            this.tabCoordinator = getTabCoordinator(poPollingChannel)
            this.tabCoordinator.init()
        }

        return this.tabCoordinator
    },
    /**
     * @returns {void}
     */
    _markPoRequest: function () {
        localStorage.setItem(poLastRequestAtKey, new Date().getTime().toString())
    },
    /**
     * @returns {boolean}
     */
    _shouldSendKeepAlive: function () {
        const lastRequestAt = parseInt(localStorage.getItem(poLastRequestAtKey), 10)
        if (Number.isNaN(lastRequestAt)) {
            return true
        }

        return new Date().getTime() - lastRequestAt >= keepAliveThresholdMinutes * millisecondsPerMinute
    },
    /**
     * @returns {void}
     */
    sendKeepAlive: function () {
        this._markPoRequest()
        $.ajax({
            url: '/po/' + this.uniqueUrlParam(),
            method: 'POST',
            data: {
                listeners: [],
            },
        })
    },
    /**
     * @returns {void}
     */
    _ensureKeepAlive: function () {
        if (this.keepAliveIntervalId !== null) {
            return
        }

        const self = this
        this.keepAliveIntervalId = window.setInterval(function () {
            if (self._getTabCoordinator().isLeader() && self._shouldSendKeepAlive()) {
                self.sendKeepAlive()
            }
        }, keepAliveCheckIntervalMinutes * millisecondsPerMinute)
    },
    /**
     * @returns {void}
     */
    shutdown: function () {
        this.abort()

        if (this.keepAliveIntervalId !== null) {
            window.clearInterval(this.keepAliveIntervalId)
            this.keepAliveIntervalId = null
        }

        if (this.tabCoordinator !== null) {
            this.tabCoordinator.destroy()
            this.tabCoordinator = null
        }

        this.listeners = {}
        this.status = false
    },
    /**
     * Decode response from server
     *
     * Call listeners who have been added
     * if data for this listeners arrives
     * @param {object} data
     * @returns {void}
     */
    _receive: function (data) {
        const self = this
        if (typeof data.listeners === 'undefined') {
            return
        }

        $.each(data.listeners, function (listener) {
            if (typeof self.listeners[listener] !== 'undefined') {
                const callbackFunction = self.listeners[listener].getCallback()
                callbackFunction(data.listeners[listener])
            }
        })
    },
    /**
     * Trigger poll to refresh all active listeners with new data
     *
     * @returns {void}
     */
    refresh: function () {
        const self = this
        if (this.xhr !== null) {
            return
        }

        const listeners = []
        for (const listener in self.listeners) {
            if (self.listeners.hasOwnProperty(listener) && self.listeners[listener].isActive()) {
                listeners.push(listener)
            }
        }

        this._markPoRequest()
        this.xhr = $.ajax({
            url: '/po/' + self.uniqueUrlParam(),
            method: 'POST',
            data: {
                listeners: listeners,
            },
            success: function (data) {
                self.xhr = null
                self._receive(data)
            },
        })
    },
    /**
     * Polls a listener.
     * The listenerName must be registered in the listeners array.
     */
    poll: function (polling) {
        const self = this
        const listenerName = polling.getListener()

        if (this.status === false) {
            window.onbeforeunload = function () {
                self.abort()
            }
            this._ensureKeepAlive()
            this.status = true
        }

        if (!this.listeners.hasOwnProperty(listenerName)) {
            this.listeners[listenerName] = polling
        }

        ;(function doPoll(listener) {
            if (!self.listeners.hasOwnProperty(listener)) {
                // The listener is not in the array anymore, so no more polling requests for this listener.
                // It can be that a listener was removed from the array.
                // Remove the entries from the localStorage from this listener.
                self._removeListenerItemsFromLocalStorage(listener)
                return
            }
            const pollingRequest = self.listeners[listener]
            const timeout = Number(pollingRequest.getTimeout())

            if (!pollingRequest.isActive()) {
                // The PO request is not "killed" in order to allow its status to be set dynamically.
                // Basically, the listener will not be triggered until the PO request is active.
                window.setTimeout(doPoll, timeout, listener)
                return
            }
            // Checks if the data in the localStorage is still valid
            const isDataValid = self._isDataValid(listener)
            // Checks if should send a PO request, to update the localStorage
            const shouldSendPO = self._shouldSendPO(listener)
            const isTabVisible = self._getTabCoordinator().isVisible()

            if (isDataValid || !shouldSendPO || !isTabVisible) {
                const data = {
                    listeners: self._readDataFromLocalStorage(listener),
                }
                self._receive(data)
                window.setTimeout(doPoll, timeout, listener)
            } else {
                self.runPolling(listener).done(function () {
                    window.setTimeout(doPoll, timeout, listener)
                    self.xhr = null
                })
            }
        })(listenerName)
    },
    /**
     * Executes one polling request.
     *
     * @param {string} listener
     */
    runPolling: function (listener) {
        const lastPoRequestAtField = this._makeLocalStorageItemName(listener, 'last-po-request-at')

        localStorage.setItem(lastPoRequestAtField, new Date().getTime().toString())
        this._markPoRequest()

        const self = this
        this.xhr = $.ajax({
            url: '/po/' + this.uniqueUrlParam(),
            method: 'POST',
            dataType: 'json',
            timeout: this.poRequestTimeout,
            data: {
                listeners: [listener],
            },
            success: function (data) {
                self._updateLocalStorage(listener, data)
                self._receive(data)
            },
        })

        return this.xhr
    },
    /**
     * Checks if the data in the local storage is up to date.
     *
     * @param {string} listener
     *
     * @returns {boolean} true, if the data in the local storage is valid
     * @private
     */
    _isDataValid: function (listener) {
        const listenerTimeout = this.listeners[listener].getTimeout()
        const currentTime = new Date().getTime()
        const poDataField = this._makeLocalStorageItemName(listener, 'po-data')
        const poDataLoadedAtField = this._makeLocalStorageItemName(listener, 'po-data-loaded-at')

        return (
            poDataField in localStorage &&
            poDataLoadedAtField in localStorage &&
            currentTime - parseInt(localStorage[poDataLoadedAtField]) < listenerTimeout
        )
    },
    /**
     * Checks if a PO request must be sent.
     * It can be that the data in the localStorage is not valid anymore, but a PO request, for the listener, was
     * already sent and the process is just waiting for feedback.
     *
     * @param {string} listener
     *
     * @returns {boolean} true if a PO request should be sent
     * @private
     */
    _shouldSendPO: function (listener) {
        const currentTime = new Date().getTime()
        const lastPoRequestAtField = this._makeLocalStorageItemName(listener, 'last-po-request-at')

        return (
            !(lastPoRequestAtField in localStorage) ||
            (lastPoRequestAtField in localStorage &&
                currentTime - parseInt(localStorage[lastPoRequestAtField]) >= this.poRequestTimeout)
        )
    },
    /**
     * Makes the localStorage item name.
     *
     * @param {string} listener
     * @param {string} itemName
     * @return {string}
     * @private
     */
    _makeLocalStorageItemName: function (listener, itemName) {
        return listener + '-' + itemName
    },
    /**
     * Updates the information in the localStorage.
     *
     * @param {string} listener
     * @param {object} data
     * @private
     */
    _updateLocalStorage: function (listener, data) {
        if (data.hasOwnProperty('listeners')) {
            const poDataField = this._makeLocalStorageItemName(listener, 'po-data')
            const poDataLoadedAtField = this._makeLocalStorageItemName(listener, 'po-data-loaded-at')

            localStorage.setItem(poDataLoadedAtField, new Date().getTime().toString())
            localStorage.setItem(poDataField, JSON.stringify(data.listeners[listener]))
        } else {
            this._removeListenerItemsFromLocalStorage(listener)
        }
    },
    /**
     * Reads the data from the localStorage and parses it into an object.
     *
     * @param {string} listener
     * @return {object}
     * @private
     */
    _readDataFromLocalStorage: function (listener) {
        const poDataField = this._makeLocalStorageItemName(listener, 'po-data')
        const data = {}

        if (localStorage.getItem(poDataField) !== null && localStorage[poDataField] !== 'undefined') {
            data[listener] = JSON.parse(localStorage[poDataField])
        }

        return data
    },
    /**
     * Removes the listener items from the localStorage.
     *
     * @param listener
     * @private
     */
    _removeListenerItemsFromLocalStorage: function (listener) {
        const poDataField = this._makeLocalStorageItemName(listener, 'po-data')
        const poDataLoadedAtField = this._makeLocalStorageItemName(listener, 'po-data-loaded-at')
        const lastPoRequestAtField = this._makeLocalStorageItemName(listener, 'last-po-request-at')

        localStorage.removeItem(poDataField)
        localStorage.removeItem(poDataLoadedAtField)
        localStorage.removeItem(lastPoRequestAtField)
    },
}

export default {
    // Get the Singleton instance if one exists
    // or create one if it doesn't
    getInstance: function () {
        return instance
    },
}
